if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        // Safety net: if more than one sticky bar ends up in the page at
        // once (e.g. the browser restoring a previous page's state via
        // back/forward cache alongside a freshly loaded page), remove any
        // others and keep only this, newest one.
        document.querySelectorAll('sticky-add-to-cart').forEach((el) => {
          if (el !== this) el.remove();
        });

        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.productFormId;
        this.button = this.querySelector('.sticky-atc__button');
        this.buttonText = this.button ? this.button.querySelector('span') : null;
        this.image = this.querySelector('.sticky-atc__image');
        this.realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.realForm = document.getElementById(this.formId);

        if (!this.realButton || !this.realForm || !this.button) return;

        // touch-action: manipulation removes the browser's default double-tap
        // delay/zoom handling on this button, which is one common cause of an
        // "I have to tap twice" feel on mobile Safari/Chrome.
        this.button.style.touchAction = 'manipulation';

        this.button.addEventListener('click', this.onClick.bind(this));

        this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
          rootMargin: '0px',
          threshold: 0,
        });
        this.observer.observe(this.realButton);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        // Proper cleanup when this element is removed (including by the
        // dedup guard above removing a duplicate), so a removed instance's
        // event subscription and observer don't keep running in the
        // background against elements that are no longer on the page.
        if (this.observer) this.observer.disconnect();
        if (typeof this.unsubscribeVariantChange === 'function') this.unsubscribeVariantChange();
        clearTimeout(this.currencyTimer);
      }

      // The bar shows only once the real Add to cart button has scrolled UP and off the top of
      // the screen. When the page first loads the button is below the fold (also "not visible"),
      // and the bar must stay hidden then, so the title and price are not covered.
      onIntersect(entries) {
        const entry = entries[0];
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('sticky-atc--visible', scrolledPast);
      }

      onClick(evt) {
        evt.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        // Reuse the real form's existing submit pathway (same validation,
        // same single network request, same cart-drawer open) instead of
        // duplicating add-to-cart logic here.
        if (typeof this.realForm.requestSubmit === 'function') {
          this.realForm.requestSubmit(this.realButton);
        } else {
          this.realButton.click();
        }
      }

      onVariantChange(event) {
        if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
        const variant = event.data.variant;

        const priceEl = this.querySelector('.sticky-atc__price');
        if (priceEl && variant) {
          this.updatePrice(priceEl, variant, event.data.html);
        }

        // Swap the sticky bar's own photo to match the newly-selected
        // variant, the same photo shown in the main gallery for that
        // variant - not the product's generic default image.
        if (this.image && variant && variant.featured_image && variant.featured_image.src) {
          this.image.src = variant.featured_image.src;
          if (this.image.srcset) this.image.removeAttribute('srcset');
          this.image.alt = variant.featured_image.alt || this.image.alt;
        }

        if (!variant) {
          this.button.setAttribute('aria-disabled', 'true');
          if (this.buttonText) this.buttonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
          return;
        }

        if (variant.available) {
          this.button.removeAttribute('aria-disabled');
          if (this.buttonText) this.buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.button.setAttribute('aria-disabled', 'true');
          if (this.buttonText) this.buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }

      // Updates the bar's price from the SERVER-RENDERED price block for the
      // newly selected variant (the same markup the main price uses), rather
      // than building the text in JavaScript. Building it here used a
      // hard-coded "$" and could not know the visitor's currency or rounding.
      updatePrice(priceEl, variant, sourceDoc) {
        const requestId = (this.priceRequestId = (this.priceRequestId || 0) + 1);

        const apply = (doc) => {
          if (requestId !== this.priceRequestId) return true; // a newer change superseded this one
          const priceRoot = doc && doc.getElementById ? doc.getElementById(`price-${this.sectionId}`) : null;
          if (!priceRoot) return false;

          const onSale = !!priceRoot.querySelector('.price--on-sale');
          const currentNode = onSale
            ? priceRoot.querySelector('.price__sale .price-item--sale')
            : priceRoot.querySelector('.price__regular .price-item--regular');
          const compareNode = onSale ? priceRoot.querySelector('.price__sale s.price-item--regular') : null;
          if (!currentNode) return false;

          this.renderPrice(priceEl, currentNode.innerHTML.trim(), compareNode ? compareNode.innerHTML.trim() : '');
          return true;
        };

        if (apply(sourceDoc)) return;

        // Fallback if the variant-change event did not include the fetched
        // page HTML: ask the server for this variant's price block directly.
        fetch(`${window.location.pathname}?variant=${variant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((text) => apply(new DOMParser().parseFromString(text, 'text/html')))
          .catch(() => {});
      }

      // Rebuilds the price using only inline elements (span / s), so it is
      // always valid inside the <p> that holds it. The price HTML inserted is
      // fresh, unconverted markup, which is then handed to the currency
      // converter.
      renderPrice(priceEl, currentHtml, compareHtml) {
        const regularLabel = priceEl.dataset.labelRegular || '';
        const saleLabel = priceEl.dataset.labelSale || '';
        let html = `<span class="visually-hidden">${regularLabel}</span>`;
        if (compareHtml) {
          html += `<s class="sticky-atc__price-compare">${compareHtml}</s>`;
          html += `<span class="visually-hidden">${saleLabel}</span>`;
        }
        html += `<span class="sticky-atc__price-current">${currentHtml}</span>`;
        priceEl.innerHTML = html;
        this.requestCurrencyConversion();
      }

      // The Bucks currency converter only re-scans the page on certain
      // events (cart requests, radio-button changes, page navigation), not
      // when a script rewrites a price. Its own reConvert() skips prices it
      // has already converted and only converts new or changed ones, so it is
      // safe to call repeatedly. Run it now and once more shortly after, as
      // Bucks itself does, in case its script is still finishing loading.
      requestCurrencyConversion() {
        clearTimeout(this.currencyTimer);
        const run = () => {
          try {
            if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') {
              window.bucksCC.reConvert();
            }
          } catch (e) {
            // Never let a third-party script's error interrupt this bar.
          }
        };
        run();
        this.currencyTimer = setTimeout(run, 900);
      }
    }
  );
}

// Extra safety net for the browser's back/forward cache: if a page is
// restored from cache (rather than freshly loaded), make sure only one
// sticky bar is left in the DOM.
window.addEventListener('pageshow', function (event) {
  if (!event.persisted) return;
  const bars = document.querySelectorAll('sticky-add-to-cart');
  bars.forEach((el, index) => {
    if (index < bars.length - 1) el.remove();
  });
});

// Extra safety net specifically for the Shopify theme editor/preview
// environment (e.g. a shopifypreview.com URL), which has its own section
// reload mechanism separate from normal page navigation: when the editor
// reloads a section (e.g. after a settings change), it can leave a
// previous instance of an element in place if that element isn't using
// Shopify's own [data-section] cleanup convention. Re-running the same
// dedup logic after any section load event covers that case regardless of
// what triggered the reload.
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function () {
    const bars = document.querySelectorAll('sticky-add-to-cart');
    bars.forEach((el, index) => {
      if (index < bars.length - 1) el.remove();
    });
  });
}